import React, { useRef, useState, useCallback } from 'react';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Avatar, 
  Typography, 
  Chip,
  IconButton,
  Fade
} from '@mui/material';
import { 
  MoreVertOutlined,
  RestaurantOutlined
} from '@mui/icons-material';
import { Restaurant, Server } from '../types';
import config from '../utils/config';

interface ChatHeaderProps {
  restaurant: Restaurant | null;
  server: Server | null;
  tableNumber: string;
  onMenuClick?: () => void;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({
  restaurant,
  server,
  tableNumber,
  onMenuClick
}) => {
  const tapCountRef = useRef(0);
  const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [headerFlash, setHeaderFlash] = useState(false);
  const warmingUpRef = useRef(false);

  const triggerWarmup = useCallback(async () => {
    if (warmingUpRef.current) return;
    warmingUpRef.current = true;

    setHeaderFlash(true);
    setTimeout(() => setHeaderFlash(false), 400);

    try {
      const relayUrl = config.gptRelayUrl + '/api/warmup';
      await fetch(relayUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ restaurantId: restaurant?.id || '' })
      });
    } catch (e) {
      // Silent fail — warmup is best-effort
    } finally {
      warmingUpRef.current = false;
    }
  }, [restaurant?.id]);

  const handleHeaderTap = useCallback(() => {
    tapCountRef.current += 1;
    if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
    if (tapCountRef.current >= 3) {
      tapCountRef.current = 0;
      triggerWarmup();
      return;
    }
    tapTimerRef.current = setTimeout(() => { tapCountRef.current = 0; }, 600);
  }, [triggerWarmup]);
  const getServerStatusColor = (status?: string) => {
    switch (status) {
      case 'active':
        return 'success';
      // case 'on_break':
      //   return 'warning';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const getServerStatusText = (status?: string) => {
    switch (status) {
      case 'active':
        return 'Online';
      // case 'on_break':
      //   return 'On Break';
      case 'inactive':
        return 'Offline';
      default:
        return 'Unknown';
    }
  };



  return (
    <AppBar 
      position="static" 
      elevation={2}
      onClick={handleHeaderTap}
      sx={{ 
        backgroundColor: headerFlash ? 'rgba(102, 126, 234, 0.15)' : 'background.paper',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
        zIndex: 1100,
        transition: 'background-color 0.3s ease',
        cursor: 'default'
      }}
    >
      <Toolbar sx={{ minHeight: '64px !important', px: 1.5 }}>
        {/* Server Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, mr: 2 }}>
          <IconButton
            onClick={(e) => { e.stopPropagation(); onMenuClick?.(); }}
            sx={{ 
              width: 40, 
              height: 40,
              mr: 2,
              backgroundColor: 'transparent',
              color: 'text.primary',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'action.hover',
              }
            }}
          >
            <MoreVertOutlined />
          </IconButton>
          
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 600,
                  fontSize: 'clamp(1rem, 4vw, 1.1rem)', // Responsive to user text size
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {server?.name || 'Your Server'}
              </Typography>
              
              {server?.status && (
                <Fade in>
                  <Chip
                    label={getServerStatusText(server.status)}
                    size="small"
                    color={getServerStatusColor(server.status) as any}
                    variant="outlined"
                    sx={{ 
                      height: 20,
                      fontSize: 'clamp(0.6rem, 2.5vw, 0.75rem)', // Responsive chip text
                      '& .MuiChip-label': {
                        px: 1
                      }
                    }}
                  />
                </Fade>
              )}
            </Box>
            
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'text.secondary',
                fontSize: 'clamp(0.7rem, 3vw, 0.85rem)', // Responsive table info
                display: 'block'
              }}
            >
              Table {tableNumber}
            </Typography>
          </Box>
        </Box>

        {/* Restaurant Info - Right Side */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'flex-end', 
            minWidth: 0, 
            mr: 2,
            maxWidth: '140px' // Prevent text from pushing avatar off screen
          }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600,
                fontSize: 'clamp(1rem, 4vw, 1.1rem)', // Match server name size
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'right',
                width: '100%'
              }}
            >
              {restaurant?.name || 'Restaurant'}
            </Typography>
            
            {restaurant?.address && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: 'clamp(0.7rem, 3vw, 0.85rem)',
                  textAlign: 'right',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  mt: 0.5,
                  lineHeight: 1.2
                }}
              >
                {restaurant.address}
              </Typography>
            )}
            
            {(restaurant?.city || restaurant?.state) && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: 'clamp(0.7rem, 3vw, 0.85rem)',
                  textAlign: 'right',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  lineHeight: 1.2
                }}
              >
                {(() => {
                  // Build city, state line
                  const parts = [];
                  if (restaurant.city) parts.push(restaurant.city);
                  if (restaurant.state) parts.push(restaurant.state);
                  return parts.join(', ');
                })()}
              </Typography>
            )}
          </Box>
          
          {/* Restaurant Avatar - matches server avatar size and mirrors layout */}
          <Avatar
            src={restaurant?.logoUrl}
            sx={{ 
              width: 40, 
              height: 40,
              backgroundColor: 'warning.main', // Yellow color to match the image
              flexShrink: 0
            }}
          >
            {restaurant?.name ? restaurant.name.charAt(0).toUpperCase() : <RestaurantOutlined />}
          </Avatar>
        </Box>
      </Toolbar>


    </AppBar>
  );
};

export default ChatHeader;
